/* eslint-disable no-console */
import React, {
  createContext, useContext, useEffect, useState, useCallback,
} from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * PiopiyContext — CHUB edition (iter 133, Apr 30 2026).
 *
 * NOTE: the "Piopiy" name is retained for back-compat with existing
 * import sites across the app (CallCustomerButton, ChatHeaderCallButton,
 * BrowserCallWidget). The underlying integration is now TeleCMI CHUB:
 *
 *   • Calls are initiated via backend `/api/telecmi/click-to-call`
 *     which hits CHUB's `/v1/agentConnect`.
 *   • CHUB rings the agent's OWN TeleCMI softphone FIRST (desktop /
 *     mobile app they already have installed), THEN dials the customer
 *     and bridges.
 *   • There is NO browser-based WebRTC audio path. The CRM drives the
 *     trigger + shows live status, but audio is on the agent's softphone.
 *
 * What this context exposes (kept API-compatible with the old PIOPIY one):
 *   creds               {configured: bool, chub_id, name, inet_no}
 *   credsLoading        bool
 *   sdkReady            always true (no SDK to load — kept for compat)
 *   online              always true when `configured` (softphone lives outside browser)
 *   call                {direction, status, phone, leadId, startedAt} | null
 *   makeCall(phone, opts)
 *   hangup()            informational only — agent hangs up on their softphone
 *   mute/unmute/hold/unhold/sendDtmf/transfer  → toasts "Use your softphone"
 *   goOnline/goOffline  → informational no-ops (softphone is always the online endpoint)
 */

const CallContext = createContext(null);

export const PiopiyProvider = ({ children }) => {
  const { user } = useAuth();
  const [creds, setCreds] = useState(null);
  const [credsLoading, setCredsLoading] = useState(true);
  const [call, setCallState] = useState(null);
  const [lastError, setLastError] = useState(null);

  // Fetch the current agent's CHUB credential status (just metadata —
  // password never leaves the server).
  const fetchCredentials = useCallback(async () => {
    if (!user) return;
    setCredsLoading(true);
    try {
      const { data } = await api.get("/telecmi/chub/my-credentials");
      setCreds(data);
    } catch (e) {
      console.error("Failed to fetch CHUB creds:", e);
      setCreds({ configured: false });
    } finally {
      setCredsLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  // makeCall — fires the backend click-to-call. Returns true on accepted.
  const makeCall = useCallback(async (phone, opts = {}) => {
    if (!creds?.configured) {
      toast.error("Set up your CHUB credentials in Settings → Telecmi first");
      return false;
    }
    const digits = String(phone || "").replace(/\D+/g, "");
    if (digits.length < 8) { toast.error("Invalid phone number"); return false; }
    if (call) { toast.error("You already have an active call trigger"); return false; }

    // Immediately surface a pending call state in the UI so the operator
    // knows the trigger fired and to pick up their softphone.
    setCallState({
      direction: "outbound",
      status: "ringing-softphone",
      phone: digits,
      leadId: opts.leadId || null,
      customerName: opts.customerName || "",
      startedAt: Date.now(),
      duration: 0,
    });

    try {
      const { data } = await api.post("/telecmi/click-to-call", {
        customer_phone: digits,
        lead_id: opts.leadId || null,
      });
      toast.success(data?.message || "Your softphone is ringing. Answer it.", { icon: "📞" });
      // After 60s, auto-clear the trigger state so it doesn't linger on
      // failed / unanswered triggers. The real call state lives in the
      // agent's softphone, not in this browser tab.
      setTimeout(() => { setCallState(null); }, 60000);
      return true;
    } catch (e) {
      console.error("click-to-call failed:", e);
      const detail = e?.response?.data?.detail || e?.message || "Click-to-call failed";
      toast.error(detail);
      setCallState(null);
      setLastError(detail);
      return false;
    }
  }, [creds, call]);

  const hangup = useCallback(() => {
    // We can't terminate the SIP leg from the browser in CHUB. Agents
    // end the call on their softphone. Clearing local state is a UX nicety.
    setCallState(null);
    toast("End the call on your softphone", { icon: "☎️" });
  }, []);

  const softphoneReminder = useCallback(() => {
    toast("Use your TeleCMI softphone for mute / hold / DTMF / transfer controls.", { icon: "☎️" });
  }, []);

  const value = {
    user,
    creds,
    credsLoading,
    sdkReady: true,                 // no SDK needed with CHUB
    online: !!creds?.configured,    // softphone is always the online endpoint when creds exist
    call,
    lastError,
    goOnline: () => toast("Your CHUB softphone is always online when it's running.", { icon: "☎️" }),
    goOffline: () => toast("Close your CHUB softphone to go offline.", { icon: "☎️" }),
    makeCall,
    answerCall: softphoneReminder,
    rejectCall: softphoneReminder,
    hangup,
    mute: softphoneReminder,
    unmute: softphoneReminder,
    hold: softphoneReminder,
    unhold: softphoneReminder,
    sendDtmf: softphoneReminder,
    transfer: softphoneReminder,
    refreshCredentials: fetchCredentials,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
};

export const usePiopiy = () => {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("usePiopiy must be used within PiopiyProvider");
  return ctx;
};
